/**
 * @link https://span.studio
 * @copyright Copyright (c) Span Studio LLC
 * @license https://github.com/Span-Studio/sta100-craft5/blob/main/LICENSE.md
 * 
 * Entry Assets.
 * UI for uploading assets for an entry. 
 * 
 * @author John Pobojewski <https://john.pobojewski.com>
 */

import Sortable from 'sortablejs';
import Modal from "/src/scripts/components/Modal.js";

class EntryAssets {
  constructor() {
    this.cnt = 0;
    this.sortableInstance = null;
  }

  init(url) {
    console.log("EntryAssets: init() - " + url);

    const stagedAssets = document.getElementById("staged-assets");

    stagedAssets.addEventListener("click", (e) => {
      if (e.target.classList.contains("delete")) {
        const { index, assetId, entryId } = e.target.dataset;
        this.deleteAssetRow(index, assetId, entryId);
      }
    });

    document.querySelector(".add-images")?.addEventListener("click", (e) => {
      e.preventDefault();
      if (document.querySelectorAll("figure.asset-row").length < 5) {
        this.createAssetRow('image', "", "", this.cnt);
      } else {
        this.cueModal("<p>You are allowed a maximum of 5 additional assets.</p>");
      }
    });

    document.querySelector(".add-video")?.addEventListener("click", (e) => {
      e.preventDefault();
      if (document.querySelectorAll("figure.asset-row").length < 5) {
        this.createAssetRow('video', "", "", this.cnt);
      } else {
        this.cueModal("<p>You are allowed a maximum of 5 additional assets.</p>");
      }
    });

    this.initSortable();
  }

  cueModal(message){
    Modal.build(message);
  }

  createAssetRow(type, fileName, fileId, cnt) {
    console.log("EntryAssets.createAssetRow(): " + cnt);

    const figure = document.createElement("figure");
    figure.className = `asset-row ui-state-default ${type}`;
    figure.dataset.index = cnt;

    const deleteBtn = document.createElement("span");
    deleteBtn.className = "delete";
    deleteBtn.dataset.index = cnt;
    deleteBtn.dataset.entryId = "new";
    deleteBtn.dataset.assetId = fileId;
    deleteBtn.textContent = "Delete asset";

    const dragHandle = document.createElement("div");
    dragHandle.className = "drag-handle";

    if (type === 'image') {
      dragHandle.innerHTML = `<span>Image</span><span class="asset-filename"></span>`;
      dragHandle.appendChild(deleteBtn);

      figure.appendChild(dragHandle);
      figure.innerHTML += `
        <input class="asset-type" type="hidden" name="fields[assets][new${cnt}][type]" value="image">
        <div class="asset-info-container">
          <span class="asset-name">
            <span class="upload-container">
              <label class="upload-label" for="upload${cnt}">Select image</label>
              <input id="upload${cnt}" class="upload-assets" type="file" name="fields[assets][new${cnt}][fields][file][]" data-count="${cnt}">
            </span>
            <img id="preview${cnt}" />
          </span>
          <textarea class="asset-caption" maxlength="250" id="caption${cnt}" name="fields[assets][new${cnt}][fields][caption]" placeholder="Caption"></textarea>
        </div>
      `;
    } else {
      dragHandle.innerHTML = `<span>Video: YouTube or Vimeo only. YouTube links must only contain one variable.</span>`;
      dragHandle.appendChild(deleteBtn);

      figure.appendChild(dragHandle);
      figure.innerHTML += `
        <input class="asset-type" type="hidden" name="fields[assets][new${cnt}][type]" value="video">
        <div class="asset-info-container">
          <input id="video${cnt}" class="video-url" type="text" name="fields[assets][new${cnt}][fields][videoUrl]" placeholder="">
          <textarea class="asset-caption" maxlength="250" id="caption${cnt}" name="fields[assets][new${cnt}][fields][caption]" placeholder="Caption"></textarea>
        </div>
      `;
    }

    document.getElementById("staged-assets").appendChild(figure);
    if (type === 'image') this.initAssetButtons(document.getElementById(`upload${cnt}`));
    this.cnt++;
  }

  readUrl(imgEl, input, callback) {
    if (input.files && input.files[0] && imgEl) {
      const file = input.files[0];
      const validTypes = ["image/gif", "image/jpeg", "image/png"];

      if (file.size < 5000000) {
        if (validTypes.includes(file.type)) {
          const reader = new FileReader();
          reader.onload = (e) => {
            imgEl.src = e.target.result;
            imgEl.srcset = "";
            callback();
          };
          reader.readAsDataURL(file);
        } else {
          this.cueModal("<p>This file is not a GIF, JPG, or PNG image.</p>");
        }
      } else {
        this.cueModal("<p>This file is bigger than 5 MB.</p>");
      }
    }
  }

  initAssetButtons(input) {
    const label = document.querySelector(`label[for='${input.id}']`);
    const caption = document.querySelector(`[data-index='${input.dataset.count}'] .drag-handle .asset-filename`);
    const labelVal = label?.innerHTML || "";

    input.addEventListener('change', (e) => {
      let fileName = e.target.value.split('\\').pop();

      if (fileName) {
        const img = document.getElementById("preview" + input.dataset.count);
        this.readUrl(img, input, () => {
          if (caption) caption.innerHTML = fileName;
          if (label) label.remove();
        });
      } else {
        if (label) {
          label.innerHTML = labelVal;
          label.classList.remove('secondary');
        }
      }
    });

    input.addEventListener('focus', () => input.classList.add('has-focus'));
    input.addEventListener('blur', () => input.classList.remove('has-focus'));
  }

  deleteAssetRow(index, deleteFileId, entryId) {
    console.log("EntryAssets.deleteAssetRow(): " + index);
    const figure = document.querySelector(`figure[data-index='${index}']`);
    if (figure) {
      const deleteBtn = figure.querySelector(".delete");
      if (deleteBtn) deleteBtn.outerHTML = '<span class="spinner"><span>Loading...</span></span>';
      figure.style.opacity = 0;
      setTimeout(() => figure.remove(), 500);
    }
  }

  updateProgress(progress) {
    const loader = document.querySelector('.assets-nav .loader-container');
    if (loader) {
      loader.classList.remove('transparent');
      const progressEl = loader.querySelector('progress');
      const textEl = loader.querySelector('span');
      if (progressEl) progressEl.value = progress;
      if (textEl) textEl.textContent = progress + '%';
    }
  }

  initSortable() {
    const container = document.getElementById("staged-assets");
    if (!this.sortableInstance) {
      this.sortableInstance = Sortable.create(container, {
        handle: ".drag-handle",
        animation: 150,
        onEnd: () => {
          document.querySelectorAll("#staged-assets figure").forEach((el, index) => {
            el.dataset.index = index;
            el.querySelectorAll("input, textarea").forEach(field => {
              if (field.name) field.name = field.name.replace(/[0-9]+/g, index);
              if (field.id) field.id = field.id.replace(/[0-9]+/g, index);
            });
            el.querySelectorAll("label").forEach(label => {
              if (label.htmlFor) label.htmlFor = label.htmlFor.replace(/[0-9]+/g, index);
            });
            el.querySelectorAll(".delete").forEach(btn => {
              btn.dataset.index = index;
            });
          });
        }
      });
    }
  }
}

export default new EntryAssets();
